import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSuggestionsApi } from "../../services/mapApis";

function MapAutoComplete({ value, placeholder, changeHandler }) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue && inputValue.length > 2 && showSuggestions) {
        fetchSuggestions(inputValue);
      } else {
        setSuggestions([]);
      }
    }, 300); // Debounce

    return () => clearTimeout(timeoutId);
  }, [inputValue, showSuggestions]);


  const fetchSuggestions = async (query) => {
    if (!query) return;
    setIsLoading(true);
    try {
      const results = await getSuggestionsApi(query);
      setSuggestions(results);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    changeHandler(newVal); // Parent update as typed
    setShowSuggestions(true);
  };

  const handleSelect = (feature) => {
    const name = feature.properties.name;
    const city = feature.properties.city;
    const state = feature.properties.state;
    const country = feature.properties.country;

    // Construct a nice display string
    const parts = [name, city, state, country].filter(Boolean);
    const formattedAddress = parts.join(", ");

    setInputValue(formattedAddress);
    changeHandler(formattedAddress);
    setShowSuggestions(false);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          className="w-full pr-10"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 bg-white">
          {suggestions.map((feature, index) => (
            <li
              key={index}
              onClick={() => handleSelect(feature)}
              className="flex cursor-default select-none items-center gap-2 px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer hover:bg-gray-100"
            >
              <MapPin className="h-4 w-4 shrink-0 opacity-50" />
              <div className="flex flex-col text-left">
                <span className="font-medium">{feature.properties.name}</span>
                <span className="text-xs text-muted-foreground">
                  {[feature.properties.city, feature.properties.state, feature.properties.country].filter(Boolean).join(", ")}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MapAutoComplete;
